import { CloseOutlined } from "@ant-design/icons";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { canNavigate } from "../utils/navigationAccess";

/** Session-only visited routes. Unsaved forms are protected by their editor. */
export function DocumentTabs({
  title,
  permissions,
  onNavigate,
}: {
  title: string;
  permissions: string[];
  onNavigate?: (route: string, label: string) => void;
}) {
  const location = useLocation(),
    navigate = useNavigate();
  const activeUrl = location.pathname + location.search + location.hash;
  const normalizeUrl = (value: string) => {
    const url = new URL(value || "/", "https://erp.invalid");
    const params = new URLSearchParams(url.search);
    params.sort();
    const sortedSearch = params.toString();
    return `${url.pathname}${sortedSearch ? `?${sortedSearch}` : ""}${url.hash}`;
  };
  const [tabs, setTabs] = useState<
    Array<{ key: string; path: string; url: string; title: string }>
  >([]);
  const url = normalizeUrl(activeUrl);
  const nav = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    if (location.pathname === "/workspace" || !canNavigate(url, permissions))
      return;
    setTabs((previous) => {
      const next = previous.filter((t) => canNavigate(t.url, permissions));
      const item = {
        key: location.pathname,
        path: location.pathname,
        url,
        title,
      };
      const index = next.findIndex((t) => t.key === item.key);
      if (index >= 0) next[index] = item;
      else next.push(item);
      return next.slice(-12);
    });
  }, [url, title, permissions]);
  useEffect(() => {
    nav.current
      ?.querySelector('[aria-current="page"]')
      ?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [tabs, activeUrl]);
  const visible = tabs.filter((t) => canNavigate(t.url, permissions));
  const activeKey = location.pathname;
  return (
    <nav ref={nav} className="erp-document-tabs" aria-label="已打开页面">
      {canNavigate("/workspace", permissions) && (
        <button
          type="button"
          className={location.pathname === "/workspace" ? "is-active" : ""}
          aria-current={location.pathname === "/workspace" ? "page" : undefined}
          onClick={() => {
            if (onNavigate) onNavigate("/workspace", "经营首页");
            else navigate("/workspace");
          }}
        >
          经营首页
        </button>
      )}
      {visible.map((tab, index) => (
        <div
          key={tab.key}
          className={`erp-document-tab ${activeKey === tab.key ? "is-active" : ""}`}
        >
          <button
            className="erp-document-tab__label"
            type="button"
            aria-current={activeKey === tab.key ? "page" : undefined}
            onClick={() => {
              if (onNavigate) onNavigate(tab.url, tab.title);
              else navigate(tab.url);
            }}
          >
            {tab.title}
          </button>
          <button
            type="button"
            className="erp-document-tab__close"
            aria-label={`关闭${tab.title}`}
            onClick={async () => {
              // Navigate first; blocked navigation must not discard the active tab.
              const isActiveTab = tab.key === activeKey;
              const nextTabs = visible.filter((item) => item.key !== tab.key);
              const fallback =
                nextTabs[index - 1]?.url ??
                nextTabs[index]?.url ??
                (canNavigate("/workspace", permissions)
                  ? "/workspace"
                  : "/access");
              if (isActiveTab) {
                if (onNavigate)
                  onNavigate(
                    fallback,
                    nextTabs[index - 1]?.title ??
                      nextTabs[index]?.title ??
                      "经营首页",
                  );
                else navigate(fallback);
              }
              setTabs((current) => current.filter((t) => t.key !== tab.key));
            }}
          >
            <CloseOutlined />
          </button>
        </div>
      ))}
    </nav>
  );
}
